/**
 * frontend/src/views/ProcessingView.ts
 * ======================================
 * Vista 2 — Pantalla de procesamiento (spinner + polling).
 * Corresponde a Sub-issues 2 y 4 de Epic 6 (manejo de errores + HITL).
 *
 * Responsabilidades:
 *   - Mostrar spinner mientras el backend procesa
 *   - Hacer polling del endpoint /estado cada 2 segundos
 *   - Transicionar automáticamente a la vista HITL o error según el estado
 *
 * Principio de independencia:
 *   No renderiza el panel de revisión. Solo muestra "cargando" y delega
 *   el resultado al orquestador (main.ts) via callbacks.
 */

import { consultarEstado } from "../api";
import type { AppState, EstadoConciliacion } from "../types";
import { icon } from "../utils/icons";

const POLL_INTERVAL_MS = 2000;

/**
 * Fallos de red consecutivos tolerados antes de abandonar.
 *
 * Un hipo puntual de red no debería tirar abajo una conciliación que el
 * backend está completando bien. Se resetea con cada respuesta exitosa.
 */
const MAX_FALLOS_CONSECUTIVOS = 5;

/**
 * Techo de duración del polling.
 *
 * Si el backend nunca sale de "procesando" (proceso caído, thread perdido), la
 * vista corta sola en vez de dejar el spinner girando para siempre.
 */
const TIMEOUT_TOTAL_MS = 10 * 60 * 1000;

export function renderProcessingView(
  container: HTMLElement,
  state: AppState,
  onEstado: (status: EstadoConciliacion, resumen: string | null, errores: string[]) => void,
  faseEnvio = false,
): void {
  container.innerHTML = "";

  const titulo = faseEnvio ? "Preparando su reporte" : "Analizando movimientos";
  const hint   = faseEnvio
    ? "Se está generando el reporte y enviando a los destinatarios configurados."
    : "ConciliA está comparando sus documentos. Usted revisará el resultado antes de aprobar el reporte.";

  const wrapper = document.createElement("div");
  wrapper.className = "processing-view";
  wrapper.innerHTML = `
    <p class="eyebrow">${faseEnvio ? "APROBACIÓN RECIBIDA · ENVÍO EN CURSO" : "CONCILIACIÓN EN CURSO"}</p>
    <div class="intelligence-pipeline" role="progressbar" aria-label="${faseEnvio ? "Generando y enviando reporte" : "Analizando documentos"}">
      <div class="pipeline-source">${icon(faseEnvio ? "file" : "bank")}<span>${faseEnvio ? "Reportes" : "Extracto"}</span></div>
      <div class="pipeline-line"></div>
      <div class="pipeline-core"><div class="illusion" aria-hidden="true"></div></div>
      <div class="pipeline-line pipeline-line--reverse"></div>
      <div class="pipeline-source">${icon(faseEnvio ? "mail" : "ledger")}<span>${faseEnvio ? "Email" : "Libro mayor"}</span></div>
    </div>
    <h2 class="processing-view__title">${titulo}</h2>
    <p class="processing-view__hint">${hint}</p>
    <div class="processing-view__concepts" aria-hidden="true">${(faseEnvio ? ["Excel", "PDF", "Email"] : ["Fechas", "Importes", "Coincidencias"]).map(t => `<span>${t}</span>`).join("")}</div>
    <p class="processing-view__footnote">${icon("info")} ${faseEnvio ? "Espere la confirmación del envío." : "El tiempo depende del volumen de movimientos."}</p>
  `;
  container.appendChild(wrapper);

  // ── Polling ───────────────────────────────────────────────────────────────
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let detenido = false;
  let enCurso = false;
  let fallosConsecutivos = 0;
  const inicio = Date.now();

  /**
   * Estados que cierran esta etapa. Dependen de la fase:
   *
   *   Fase 1 — "esperando_aprobacion" es la meta: el usuario debe decidir.
   *   Fase 2 — la decisión ya se envió. Si el backend todavía informa
   *            "esperando_aprobacion" es porque el worker aún no la procesó,
   *            así que hay que seguir consultando, no dar la etapa por cerrada.
   */
  const ESTADOS_FINALES: readonly EstadoConciliacion[] = faseEnvio
    ? ["completado", "cancelado", "error"]
    : ["esperando_aprobacion", "completado", "cancelado", "error"];

  async function poll(): Promise<void> {
    // `enCurso` evita que dos consultas se solapen cuando el backend tarda más
    // que el intervalo. Sin esto, dos respuestas podían resolver a la vez y
    // disparar dos transiciones de vista para el mismo evento.
    if (detenido || enCurso || !state.threadId) return;

    if (Date.now() - inicio > TIMEOUT_TOTAL_MS) {
      finalizar("error", null, [
        "El proceso superó el tiempo máximo de espera. " +
        "Verifique que el servidor siga en funcionamiento e intente nuevamente.",
      ]);
      return;
    }

    enCurso = true;
    try {
      const resp = await consultarEstado(state.threadId);
      fallosConsecutivos = 0;

      if (ESTADOS_FINALES.includes(resp.status)) {
        finalizar(resp.status, resp.resumen ?? null, resp.errores ?? []);
      }
      // Si sigue "procesando", el intervalo continúa.
    } catch {
      fallosConsecutivos += 1;

      if (fallosConsecutivos >= MAX_FALLOS_CONSECUTIVOS) {
        finalizar("error", null, [
          "Se perdió la comunicación con el servidor. " +
          "Verifique que esté en funcionamiento e intente nuevamente.",
        ]);
      }
      // Con menos fallos, reintenta en el próximo tick.
    } finally {
      enCurso = false;
    }
  }

  function detenerPolling(): void {
    detenido = true;
    if (intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  }

  function finalizar(
    status: EstadoConciliacion,
    resumen: string | null,
    errores: string[],
  ): void {
    // Guarda de idempotencia: una consulta que ya estaba en vuelo puede resolver
    // después de que otra finalizó. Sin este corte, notificaría un estado viejo
    // y haría retroceder la vista a una etapa ya superada.
    if (detenido) return;

    detenerPolling();
    onEstado(status, resumen, errores);
  }

  // Primera consulta inmediata, luego cada POLL_INTERVAL_MS
  void poll();
  intervalId = setInterval(poll, POLL_INTERVAL_MS);
}
